import { inject, Observer } from "mobx-react"
import { WithRouterProps } from "next/dist/client/with-router"
import Head from "next/head"
import { withRouter } from "next/router"
import React from "react"
import styled, { css } from "styled-components"

import CtaLink from "~/components/atoms/CtaLink"
import FunctionLink from "~/components/atoms/FunctionLink"
import TransitionLink from "~/components/atoms/TransitionLink"
import Typography from "~/components/atoms/Typography"
import { ContainerLarge } from "~/components/layouts/Container"
import CourseApplyDoneModal from "~/components/organisms/CourseApplyDoneModal"
import CourseInputPersonalDataModal from "~/components/organisms/CourseInputPersonalDataModal"
import CourseSchduleSelectModal from "~/components/organisms/CourseSchduleSelectModal"
import Footer from "~/components/organisms/Footer"
import Header from "~/components/organisms/Header"
import InternshipGuarantee from "~/components/organisms/course-detail/InternshipGuarantee"
import { Color, IsMobile, IsPC, LineHeight, RadiusSize, Shadow, SpacingSize, TextSize } from "~/consts/style-mixins"
import { Course } from "~/entities/Course"
import { TmpUserInfo } from "~/entities/User"
import { staticPath } from "~/services/helper"
import ApplicationStore from "~/stores/ApplicationStore"
import RootStore from "~/stores/RootStore"
import UiStore from "~/stores/UiStore"
import UserStore from "~/stores/UserStore"

interface StyledProps {
  open: boolean
  course: Course
  uiStore: UiStore
  userStore: UserStore
  applicationStore: ApplicationStore
}

interface State {
  isLoading: boolean
  selectedScheduleValues: string[]
  tmpUserInfo: TmpUserInfo
}

@inject("applicationStore", "uiStore", "userStore")
class WebProgrammingCourses extends React.Component<WithRouterProps & StyledProps, State> {
  static async getInitialProps({ initialStoreData }: { initialStoreData: RootStore }) {
    const { courseStore } = initialStoreData
    const course = await courseStore.fetchById(1)
    return { course }
  }

  constructor(props: WithRouterProps & StyledProps) {
    super(props)
    const { uiStore } = props
    switch (props.router.query.openModal) {
      case "selectSchedule":
        uiStore.toggleCourseSchduleSelectModal()
        break
      case "confirmApply":
        uiStore.toggleConfirmCourseApplyModal()
        break
      case "applyComplete":
        uiStore.toggleCompleteApplyCourseModal()
        break
    }
  }

  state = {
    isLoading: false,
    selectedScheduleValues: [],
    tmpUserInfo: {
      name: null,
      phoneNumber: null,
    },
  }

  render() {
    const { course, applicationStore, uiStore, userStore } = this.props
    const { isLoading, selectedScheduleValues, tmpUserInfo } = this.state
    const internships = [
      {
        id: "7104",
        title: "【国内最大級動画メディア】開発・分析に挑戦するエンジニアインターン(新卒採用直結) ",
        companyName: "株式会社エブリー",
        logoImgSrc:
          "https://s3-ap-northeast-1.amazonaws.com/in-fra/company/main_img/61f839ec9d74e9bd5285b77fddccbcf5_production",
        thumbnailImgSrc:
          "https://s3-ap-northeast-1.amazonaws.com/in-fra/intern/thumbnail/U09DE5MHSCzGuWh38JIOhwzkakqPXwQv.jpeg",
      },
      {
        id: "8569",
        title: "【リモートOK／最新技術が学べる】フロントエンドエンジニアインターン募集！",
        companyName: "イーストフィールズ株式会社",
        logoImgSrc: "https://s3-ap-northeast-1.amazonaws.com/in-fra/company/dqiHc4JDa2tU9I4bRpbatSYf89YGRcJr.png",
        thumbnailImgSrc: "https://in-fra.s3-ap-northeast-1.amazonaws.com/intern/ZeWHUxVg8h3yOIeH32EdKPH9rFJyCrut.jpeg",
      },
      {
        id: "8240",
        title: "Golang・Kotlin・Swiftなど最先端技術を駆使してWebサービス・動画配信アプリを開発！",
        companyName: "株式会社Donuts",
        logoImgSrc: "https://s3-ap-northeast-1.amazonaws.com/in-fra/company/QEpK5u25pDzwb7KpoccyTL9wX6hOpE7Z.jpeg",
        thumbnailImgSrc:
          "https://s3-ap-northeast-1.amazonaws.com/in-fra/intern/thumbnail/qmQqVkXljkA3OVxFNGar26vFD79WLEYO.png",
      },
    ]

    const handleSelectSchedule = (schedules: string[]) => {
      this.setState({
        selectedScheduleValues: schedules,
      })
    }

    const toggleLoading = () => {
      this.setState({
        isLoading: !this.state.isLoading,
      })
    }

    const updateTmpUserInfo = (values: TmpUserInfo) => {
      this.setState({
        tmpUserInfo: values,
      })
    }

    const mainActionForSchduleSelectModal = async () => {
      toggleLoading()
      selectedScheduleValues.sort()
      if (userStore.authed) {
        await applicationStore.courseConsultationApply(1, selectedScheduleValues.sort())
      } else {
        await applicationStore.courseConsultationApplyByGuest(1, tmpUserInfo, selectedScheduleValues.sort())
      }
      uiStore.toggleCourseSchduleSelectModal()
      uiStore.toggleCompleteApplyCourseModal()
      toggleLoading()
    }

    const PriceContent = () => {
      return (
        <StyledContentItemPriceArea>
          <StyledContentItemPriceSupplement type="LargeLabel">12回の分割払いで</StyledContentItemPriceSupplement>
          <StyledPriceItemCardPrice>
            <StyledPriceItemCardPriceTax>税込&nbsp;</StyledPriceItemCardPriceTax>
            ¥24,750
            <StyledPriceItemCardPer>/月</StyledPriceItemCardPer>
          </StyledPriceItemCardPrice>
          <StyledPriceItemCardPriceSumWrapper>
            <StyledPriceItemCardPriceSum>総額 ¥297,000（税込）</StyledPriceItemCardPriceSum>
          </StyledPriceItemCardPriceSumWrapper>
        </StyledContentItemPriceArea>
      )
    }

    return (
      <>
        <Head>
          <title>プログラミング講座 | Infra（インフラ）</title>
          <meta
            name="description"
            content={`情報系学部ではない学生でも3ヶ月で網羅的にWebプログラミングを習得！就職に繋がるプログラミング講座でエンジニアへの一歩を踏み出そう！`}
          />
          <meta name="og:title" content="プログラミング講座 | Infra（インフラ）" />
          <meta
            name="og:description"
            content="情報系学部ではない学生でも3ヶ月で網羅的にWebプログラミングを習得！就職に繋がるプログラミング講座でエンジニアへの一歩を踏み出そう！"
          />
          <meta
            name="og:image"
            content={process.env.APP_HOST + staticPath("/static/images/course/course_engineer_feature_1.png")}
          />
        </Head>
        <Header logoType="Courses" showTab />
        <Container>
          <StyledOverGrayArea>
            <StyledMainVisual>
              <StyledMainVisualInner>
                <div>
                  <StyledMainVisualText>
                    {/* <StyledMainVisualSupplement>
                      2021年6月26日(土) キックオフ
                    </StyledMainVisualSupplement>
                    <img
                      src={staticPath(
                        "/static/images/course/course_decoration-short.svg"
                      )}
                    /> */}
                    <StyledMainVisualTitle>
                      Infraプログラミング講座で
                      <br />
                      新卒エンジニアとして内定を
                      <br />
                      獲得できるレベルへ
                    </StyledMainVisualTitle>
                    <StyledMainVisualSubTitleWrapper>
                      <StyledMainVisualSubTitleLeft>
                        参加
                        <br />
                        保証
                      </StyledMainVisualSubTitleLeft>
                      <StyledMainVisualSubTitleRight>
                        講座修了後に
                        <StyledMainVisualSubTitleRightBold>インターンへの参加を保証</StyledMainVisualSubTitleRightBold>*
                        <br />
                        実務経験を通して更に成長できる環境をご用意
                      </StyledMainVisualSubTitleRight>
                    </StyledMainVisualSubTitleWrapper>
                    <InternshipGuarantee color="White" linkColor="White" />
                    {/* <StyledMainVisualSubTitleWrapper>
                      <StyledMainVisualSubTitleRight>
                        卒業生の約8割が長期インターンに合格<br />
                        講座修了後のインターン紹介まで徹底サポートします<br />
                        <Typography type="SubNote" colorProp="#FFFFFF">
                          * 第6期生（2020年12月〜）の実績です
                        </Typography>
                      </StyledMainVisualSubTitleRight>
                    </StyledMainVisualSubTitleWrapper> */}
                  </StyledMainVisualText>
                </div>
                <StyledTopButtonGroup>
                  <StyledCtaLink
                    size="Medium"
                    href={course.lineUrl}
                    target="_brank"
                    className="gtm_course-waiting-fot-next-btn"
                  >
                    <StyledApplyMethodLine src={staticPath("/static/images/course/course_line_green.svg")} />
                    次回の開講通知を受け取る
                  </StyledCtaLink>
                  {/* <StyledApplyMethod>
                    参加者限定！最大2万円OFFクーポン進呈
                  </StyledApplyMethod>
                  <StyledFunctionLink
                    size="Medium"
                    isButton
                    onClick={() => {
                      userStore.user
                        ? uiStore.toggleCourseSchduleSelectModal()
                        : uiStore.toggleAuthPromptModal(
                            internalLink(`/auth/register?redirect_after_auth=${location.pathname}?register-complete_from=consultation`)
                          )
                    }}
                    className="gtm_engineer-course-application"
                  >
                    無料相談会を予約する
                  </StyledFunctionLink> */}
                </StyledTopButtonGroup>
              </StyledMainVisualInner>
              <StyledBeginner>
                <img src={staticPath("/static/images/course/course_label.svg")} />
                <p className="text">
                  初心者
                  <br />
                  文系OK
                </p>
              </StyledBeginner>
            </StyledMainVisual>
            <StyledContainerLarge>
              <StyledContainer>
                <StyledContainerTextArea>
                  <StyledContainerTextAreaInner>
                    <StyledContainerLabel>BACKGROUND</StyledContainerLabel>
                    <StyledContainerHeading>
                      新卒でも開発経験・スキルが
                      <br />
                      求められる時代に
                    </StyledContainerHeading>
                    <StyledContainerCaption type="Body">
                      変化が激しく不確実性の高い今日、
                      <br />
                      終身雇用を前提とした未経験のエンジニア就職は
                      <br />
                      厳しくなりつつある
                    </StyledContainerCaption>
                  </StyledContainerTextAreaInner>
                </StyledContainerTextArea>
                <StyledContainerMainArea>
                  <StyledBackgroundHeading type="SubHeading">変化が激しく不確実性の高い時代</StyledBackgroundHeading>
                  <StyledBackgroundItemArea>
                    <StyledBackgroundItem>
                      <StyledBackgroundItemIcon src={staticPath("/static/images/course/course_background-item1.svg")} />
                      <StyledBackgroundItemText type="SubHeading">
                        年功序列・
                        <br />
                        終身雇用制度の限界
                      </StyledBackgroundItemText>
                    </StyledBackgroundItem>
                    <StyledBackgroundItem>
                      <StyledBackgroundItemIcon src={staticPath("/static/images/course/course_background-item2.svg")} />
                      <StyledBackgroundItemText type="SubHeading">
                        企業に未経験を育てる余裕がなくなる
                      </StyledBackgroundItemText>
                    </StyledBackgroundItem>
                  </StyledBackgroundItemArea>
                  <StyledBackgroundArrowWrapper>
                    <StyledBackgroundArrow />
                  </StyledBackgroundArrowWrapper>
                  <StyledBackgroundAfter>
                    <StyledBackgroundAfterSupplement>ポテンシャル採用から</StyledBackgroundAfterSupplement>
                    スキルや経験重視の採用へ
                  </StyledBackgroundAfter>
                </StyledContainerMainArea>
              </StyledContainer>
            </StyledContainerLarge>
          </StyledOverGrayArea>
          {/* <StyledContainerLarge>
            <StyledContainer>
              <StyledContainerTextArea>
                <StyledContainerTextAreaInner>
                  <StyledContainerLabel>FLOW</StyledContainerLabel>
                  <StyledContainerHeading>
                    実践スキルを身につけ
                    <br />
                    就職を1歩リード
                  </StyledContainerHeading>
                  <StyledContainerCaption type="Body">
                    講座で知識・スキルを身に付けたら、
                    <br />
                    修了後は長期インターンで実務経験を積んで
                    <br />
                    就職活動に挑むのがオススメ
                  </StyledContainerCaption>
                </StyledContainerTextAreaInner>
              </StyledContainerTextArea>
              <StyledContainerMainArea>
                <EmploymentFlow
                  courseName="プログラミング講座"
                  arrowBottomMargin="-283"
                />
              </StyledContainerMainArea>
            </StyledContainer>
          </StyledContainerLarge> */}
          <StyledContainerLarge>
            <StyledContainer>
              <StyledContainerTextArea>
                <StyledContainerTextAreaInner>
                  <StyledContainerLabel>SOLUTION</StyledContainerLabel>
                  <StyledContainerHeading>
                    インターンでの実務開発を
                    <br />
                    取り入れたカリキュラムで
                    <br />
                    成長をサポート
                  </StyledContainerHeading>
                  <StyledContainerCaption type="Body">
                    講座修了後にインターンへの参加を保証*
                    <br />
                    採用市場で求められるレベルまでスキルアップ
                  </StyledContainerCaption>
                  <InternshipGuarantee color="Gray" />
                </StyledContainerTextAreaInner>
              </StyledContainerTextArea>
              <StyledContainerMainArea>
                <StyledSolutionFlow>
                  <StyledSolutionFlowBackgroundGroup>
                    <StyledSolutionFlowBackground />
                    <StyledSolutionFlowBackground />
                  </StyledSolutionFlowBackgroundGroup>
                  <StyledSolutionFlowBranchGroup>
                    <StyledSolutionFlowBranchItem>Infra</StyledSolutionFlowBranchItem>
                    <StyledSolutionFlowBranchItem>一般的なスクール</StyledSolutionFlowBranchItem>
                  </StyledSolutionFlowBranchGroup>
                  <StyledSolutionFlowItem type="SubHeading">基礎を学ぶ</StyledSolutionFlowItem>
                  <StyledSolutionFlowItem type="SubHeading">サンプルアプリを作る</StyledSolutionFlowItem>
                  <StyledSolutionFlowItem type="SubHeading">オリジナルアプリを作る</StyledSolutionFlowItem>
                  <StyledSolutionFlowItemHalf type="SubHeading">
                    <StyledSolutionFlowItemHalfLabel>参加保証</StyledSolutionFlowItemHalfLabel>
                    インターンで
                    <br />
                    実務経験を積む
                  </StyledSolutionFlowItemHalf>
                  <StyledSolutionArrowGroup>
                    <StyledSolutionArrowWrapper>
                      <StyledSolutionArrow />
                    </StyledSolutionArrowWrapper>
                    <StyledSolutionArrowWrapper>
                      <StyledSolutionArrow />
                    </StyledSolutionArrowWrapper>
                  </StyledSolutionArrowGroup>
                  <StyledSolutionFlowItemLastGroup>
                    <StyledSolutionFlowItemLastItem type="SubHeading">
                      希望の企業に
                      <br />
                      エンジニア職入社
                    </StyledSolutionFlowItemLastItem>
                    <StyledSolutionFlowItemLastItem type="SubHeading">
                      未経験可の企業
                      <br />
                      にしか入れない
                    </StyledSolutionFlowItemLastItem>
                  </StyledSolutionFlowItemLastGroup>
                </StyledSolutionFlow>
                <StyledSolutionGraph>
                  <StyledSolutionGraphHeading type="Heading">
                    インターンでの実務を経験して
                    <br />
                    希望の企業に就職できるスキルレベルに
                  </StyledSolutionGraphHeading>
                  <StyledSolutionGraphImagePc src={staticPath("/static/images/course/course_solution-graph-pc.png")} />
                  <StyledSolutionGraphImageSp src={staticPath("/static/images/course/course_solution-graph-sp.png")} />
                  <StyledSolutionGraphDescription>
                    <StyledSolutionGraphDescriptionLeft>
                      到達
                      <br />
                      レベル
                    </StyledSolutionGraphDescriptionLeft>
                    <StyledSolutionGraphDescriptionRight>
                      <StyledSolutionGraphDescriptionRightEmphasis>
                        ①希望の企業にエンジニア就職できる
                      </StyledSolutionGraphDescriptionRightEmphasis>
                      <br />
                      ②未経験可の企業に就職できる
                    </StyledSolutionGraphDescriptionRight>
                  </StyledSolutionGraphDescription>
                </StyledSolutionGraph>
              </StyledContainerMainArea>
            </StyledContainer>
          </StyledContainerLarge>
          <StyledPromoteArea>
            <StyledPromoteAreaInner>
              <div>
                <StyledPromoteMessage type="SubHero">
                  まずは相談会でアドバイザーに
                  <StyledBrSp />
                  詳しく聞いてみよう！
                </StyledPromoteMessage>
                <StyledPromoteCaption type="SubNote">
                  オンラインでスキマ時間に30分！
                  <StyledBrSp />
                  プログラミングに関するあなたのお悩みに
                  <StyledBrSp />
                  何でもお答えします。
                </StyledPromoteCaption>
              </div>
              <StyledButtonGroup>
                <StyledCtaLink
                  size="Medium"
                  href="http://bit.ly/2vN2HhZ"
                  target="_brank"
                  className="gtm_course-waiting-fot-next-btn"
                >
                  <StyledApplyMethodLine src={staticPath("/static/images/course/course_line_green.svg")} />
                  次回の開講通知を受け取る
                </StyledCtaLink>
                {/* <StyledApplyMethod>
                  参加者限定！最大2万円OFFクーポン進呈
                </StyledApplyMethod>
                <StyledFunctionLink
                  size="Medium"
                  isButton
                  onClick={() => {
                    userStore.user
                      ? uiStore.toggleCourseSchduleSelectModal()
                      : uiStore.toggleAuthPromptModal(
                          internalLink(`/auth/register?redirect_after_auth=${location.pathname}?register-complete_from=consultation`)
                        )
                  }}
                  className="gtm_engineer-course-application"
                >
                  無料相談会を予約する
                </StyledFunctionLink> */}
              </StyledButtonGroup>
            </StyledPromoteAreaInner>
          </StyledPromoteArea>
          <StyledContainerLarge>
            <StyledContainer>
              <StyledContainerTextArea>
                <StyledContainerTextAreaInner>
                  <StyledContainerLabel>GRADUATES VOICE</StyledContainerLabel>
                  <StyledContainerHeading>
                    多数の卒業生が
                    <br />
                    長期インターンに合格しています
                  </StyledContainerHeading>
                </StyledContainerTextAreaInner>
              </StyledContainerTextArea>
              <StyledContainerMainArea>
                <GraduatesContainer>
                  <div className="each-graduate">
                    <img
                      src={staticPath("/static/images/course/programming/graduate-3.jpg")}
                      className="graduate-thumbnail"
                      alt=""
                    />
                    <div className="each-graduate-right">
                      <p className="comment">
                        学習から面接まであらゆる面のサポートが充実していました。実務経験に繋がる講座は限られているため、受講して良かったと感じます。
                      </p>
                      <p className="profile">
                        慶應義塾大学 文学部 23卒 Y.Kさん
                        <br />
                        インターン先：サイバーウェーブ株式会社
                      </p>
                    </div>
                  </div>
                  <div className="each-graduate">
                    <img
                      src={staticPath("/static/images/course/programming/graduate-1.jpg")}
                      className="graduate-thumbnail"
                      alt=""
                    />
                    <div className="each-graduate-right">
                      <p className="comment">
                        WEB制作は未経験でしたが、丁寧に教えて頂きました。講座ではメジャーなツールやフレームワークを学ぶことができ、実務でも活かせています。
                      </p>
                      <p className="profile">
                        千葉大学 教育学部 23卒 T.Cさん
                        <br />
                        インターン先：チャットプラス株式会社
                      </p>
                    </div>
                  </div>
                  <div className="each-graduate">
                    <img
                      src={staticPath("/static/images/course/programming/graduate-2.jpg")}
                      className="graduate-thumbnail"
                      alt=""
                    />
                    <div className="each-graduate-right">
                      <p className="comment">
                        独学では解決するのが難しいエラーもメンターさんのおかげで効率よく解決でき、講座後ではアプリ開発の全体的な流れを理解できました。
                      </p>
                      <p className="profile">
                        東京理科大学 経営学部 23卒 R.Gさん
                        <br />
                        インターン先：株式会社Her’s
                      </p>
                    </div>
                  </div>
                </GraduatesContainer>
              </StyledContainerMainArea>
            </StyledContainer>
          </StyledContainerLarge>
          <StyledPromoteArea>
            <StyledPromoteAreaInner>
              <div>
                <StyledPromoteMessage type="SubHero">
                  まずは相談会でアドバイザーに
                  <StyledBrSp />
                  詳しく聞いてみよう！
                </StyledPromoteMessage>
                <StyledPromoteCaption type="SubNote">
                  オンラインでスキマ時間に30分！
                  <StyledBrSp />
                  プログラミングに関するあなたのお悩みに
                  <StyledBrSp />
                  何でもお答えします。
                </StyledPromoteCaption>
              </div>
              <StyledButtonGroup>
                <StyledCtaLink
                  size="Medium"
                  href="http://bit.ly/2vN2HhZ"
                  target="_brank"
                  className="gtm_course-waiting-fot-next-btn"
                >
                  <StyledApplyMethodLine src={staticPath("/static/images/course/course_line_green.svg")} />
                  次回の開講通知を受け取る
                </StyledCtaLink>
                {/* <StyledApplyMethod>
                  参加者限定！最大2万円OFFクーポン進呈
                </StyledApplyMethod>
                <StyledFunctionLink
                  size="Medium"
                  isButton
                  onClick={() => {
                    userStore.user
                      ? uiStore.toggleCourseSchduleSelectModal()
                      : uiStore.toggleAuthPromptModal(
                          internalLink(`/auth/register?redirect_after_auth=${location.pathname}?register-complete_from=consultation`)
                        )
                  }}
                  className="gtm_engineer-course-application"
                >
                  無料相談会を予約する
                </StyledFunctionLink> */}
              </StyledButtonGroup>
            </StyledPromoteAreaInner>
          </StyledPromoteArea>
          <StyledContainerLarge>
            <StyledContainer>
              <StyledContainerTextArea>
                <StyledContainerTextAreaInner>
                  <StyledContainerLabel>CONTENT</StyledContainerLabel>
                  <StyledContainerHeading>
                    実務に必要なスキルを身につける
                    <br />
                    プログラミング講座の内容
                  </StyledContainerHeading>
                  <StyledContainerCaption type="Body">
                    エンジニア就職はスキルが求められる
                    <br />
                    情報系学生にも負けない実力がつく
                  </StyledContainerCaption>
                </StyledContainerTextAreaInner>
              </StyledContainerTextArea>
              <StyledContainerMainArea>
                <StyledContentItem>
                  <StyledContentItemSubTitle>新卒エンジニアを目指す人向け</StyledContentItemSubTitle>
                  <StyledContentItemTitle type="Hero">
                    インターン
                    <StyledBrSp />
                    参加保証コース
                  </StyledContentItemTitle>
                  <StyledPriceContentWrapeer>
                    <PriceContent />
                  </StyledPriceContentWrapeer>
                  <StyledContentItemPointLabel>こんな方におすすめ</StyledContentItemPointLabel>
                  <StyledContentItemPointInner>
                    <StyledContentItemPointRecomends>
                      <li>エンジニア就職を見据えて長期インターンに行きたいがなかなか受からない</li>
                      <li>新卒でWeb系ベンチャーなど自社サービス開発に携わるエンジニアになりたい</li>
                      <li>エンジニア職として就職を視野に入れているがプログラミング経験がない</li>
                    </StyledContentItemPointRecomends>
                  </StyledContentItemPointInner>
                  <StyledContentItemPointLabel>講座内容</StyledContentItemPointLabel>
                  <StyledContentItemPointInner>
                    <StyledContentItemTitleWrapper>
                      <StyledContentItemSpFlexArea>
                        <StyledContentItemLabel>35日間</StyledContentItemLabel>
                        <StyledContentItemStep type="SubHero">基礎講座</StyledContentItemStep>
                      </StyledContentItemSpFlexArea>
                      <StyledContentItemDescription>独自セレクトした動画で効率良く学習</StyledContentItemDescription>
                    </StyledContentItemTitleWrapper>
                    <StyledContentItemCaption type="Body">
                      HTML/CSSなどの基礎からJavaScript/PHPを用いた動的なページの実装まで、実務で求められる技術を包括的に学びます。動画での不明点はいつでもメンター（現役エンジニア）に質問し、解決できます。
                    </StyledContentItemCaption>
                    <StyledContentArrowWrapper>
                      <StyledContentArrow />
                    </StyledContentArrowWrapper>
                    <StyledContentItemTitleWrapper>
                      <StyledContentItemSpFlexArea>
                        <StyledContentItemLabel>65日間</StyledContentItemLabel>
                        <StyledContentItemStep type="SubHero">実践講座</StyledContentItemStep>
                      </StyledContentItemSpFlexArea>
                      <StyledContentItemDescription>自作プロダクトを企画・開発</StyledContentItemDescription>
                    </StyledContentItemTitleWrapper>
                    <StyledContentItemCaption type="Body">
                      実際にプロダクトを企画し、開発を行います。実践形式を通じて、実務で求められるスキルを身に付けながらプログラミングの理解を深めます。
                    </StyledContentItemCaption>
                    <StyledContentArrowWrapper>
                      <StyledContentArrow />
                    </StyledContentArrowWrapper>
                    <StyledContentItemTitleWrapper>
                      <StyledContentItemSpFlexArea>
                        <StyledContentItemLabel>3ヶ月〜</StyledContentItemLabel>
                        <StyledContentItemStep type="SubHero">インターン</StyledContentItemStep>
                      </StyledContentItemSpFlexArea>
                      <StyledContentItemDescription>実務経験で更なる成長を</StyledContentItemDescription>
                    </StyledContentItemTitleWrapper>
                    <StyledContentItemCaption type="Body">
                      提携先の企業様、もしくは本講座を運営する㈱Legaseedの受託開発事業にてエンジニアとして長期インターンへの参加を保証*。実務での開発経験が飛躍的な成長につながります。
                      <InternshipGuarantee color="Gray" />
                    </StyledContentItemCaption>
                  </StyledContentItemPointInner>
                </StyledContentItem>
                <StyledFeatureArrowWrapper>
                  <StyledFeatureArrow />
                </StyledFeatureArrowWrapper>
                <StyledFeatureItem>
                  <StyledFeatureInternsHeading>
                    講座とインターンでの開発実績を持って
                    <br />
                    新卒エンジニアとして就職活動に挑もう
                  </StyledFeatureInternsHeading>
                </StyledFeatureItem>
              </StyledContainerMainArea>
            </StyledContainer>
          </StyledContainerLarge>
          <StyledPromoteArea>
            <StyledPromoteAreaInner>
              <div>
                <StyledPromoteMessage type="SubHero">
                  まずは相談会でアドバイザーに
                  <StyledBrSp />
                  詳しく聞いてみよう！
                </StyledPromoteMessage>
                <StyledPromoteCaption type="SubNote">
                  オンラインでスキマ時間に30分！
                  <StyledBrSp />
                  プログラミングに関するあなたのお悩みに
                  <StyledBrSp />
                  何でもお答えします。
                </StyledPromoteCaption>
              </div>
              <StyledButtonGroup>
                <StyledCtaLink
                  size="Medium"
                  href="http://bit.ly/2vN2HhZ"
                  target="_brank"
                  className="gtm_course-waiting-fot-next-btn"
                >
                  <StyledApplyMethodLine src={staticPath("/static/images/course/course_line_green.svg")} />
                  次回の開講通知を受け取る
                </StyledCtaLink>
                {/* <StyledApplyMethod>
                  参加者限定！最大2万円OFFクーポン進呈
                </StyledApplyMethod>
                <StyledFunctionLink
                  size="Medium"
                  isButton
                  onClick={() => {
                    userStore.user
                      ? uiStore.toggleCourseSchduleSelectModal()
                      : uiStore.toggleAuthPromptModal(
                          internalLink(`/auth/register?redirect_after_auth=${location.pathname}?register-complete_from=consultation`)
                        )
                  }}
                  className="gtm_engineer-course-application"
                >
                  無料相談会を予約する
                </StyledFunctionLink> */}
              </StyledButtonGroup>
            </StyledPromoteAreaInner>
          </StyledPromoteArea>
          <StyledContainerLarge>
            <StyledContainer>
              <StyledContainerTextArea>
                <StyledContainerTextAreaInner>
                  <StyledContainerLabel>SUPPORT</StyledContainerLabel>
                  <StyledContainerHeading>
                    現役のエンジニアによる
                    <br />
                    充実の指導・サポート体制
                  </StyledContainerHeading>
                  <StyledContainerCaption type="Body">文系学生や初心者でも挫折せずにやり切れる</StyledContainerCaption>
                </StyledContainerTextAreaInner>
              </StyledContainerTextArea>
              <StyledContainerMainArea>
                <StyledFeatureItem>
                  <StyledFeatureItemImage2 />
                  <StyledFeatureItemTitle type="SubHero">
                    現役エンジニアが講座修了まで徹底サポート
                  </StyledFeatureItemTitle>
                  <StyledFeatureItemCaption type="Body">
                    現役エンジニアが専属メンターとなり、開発環境構築からオリジナルWebサービスの作成まで初心者が挫折しやすい部分を手厚くサポートするので最後までやり切ることができます。
                  </StyledFeatureItemCaption>
                </StyledFeatureItem>
                <StyledFeatureItem>
                  <StyledFeatureItemImage1 />
                  <StyledFeatureItemTitle type="SubHero">24時間いつでもチャットで質問可能</StyledFeatureItemTitle>
                  <StyledFeatureItemCaption type="Body">
                    分からないことは時間を気にせずチャットですぐに質問が可能。日中帯であれば平均1時間以内でメンターから返信がもらえます。
                  </StyledFeatureItemCaption>
                </StyledFeatureItem>
                {/* <StyledFeatureItem>
                  <StyledFeatureItemImage4 />
                  <StyledFeatureItemTitle type="SubHero">
                    自習室で現役エンジニアにいつでも質問可
                  </StyledFeatureItemTitle>
                  <StyledFeatureItemCaption type="Body">
                    2020年9月より代官山に自習室をオープン。フリーランス歴10年の現役エンジニアが常駐しているので、分からないことを直接質問して効率良く学習を進めることができます。
                  </StyledFeatureItemCaption>
                </StyledFeatureItem> */}
                <StyledFeatureItem>
                  <StyledFeatureItemImage3 />
                  <StyledFeatureItemTitle type="SubHero">講座修了後もインターン探しを後押し</StyledFeatureItemTitle>
                  <StyledFeatureItemCaption type="Body">
                    講座修了後はInfraインターンが保有する豊富な長期インターン求人からあなたに合ったエンジニア求人を紹介。中には本講座の卒業生を優遇していただける企業様もございます。求人紹介だけでなく選考対策やポートフォリオ作りまでサポートします。
                    <InternshipGuarantee color="Gray" />
                  </StyledFeatureItemCaption>
                </StyledFeatureItem>
                {/* <StyledFeatureArrowWrapper>
                  <StyledFeatureArrow />
                </StyledFeatureArrowWrapper>
                <StyledFeatureItem>
                  <StyledFeatureInternsHeading>
                    開発したプロダクトとコードを持って
                    <br />
                    こんなインターンに挑戦できます
                  </StyledFeatureInternsHeading>
                  {internships.map((internship, i) => (
                    <StyledFeatureInternItem
                      key={i}
                      href={`${process.env.INFRA_INTERN_URL}/intern-list/${internship.id}`}
                    >
                      <StyledFeatureInternItemImage
                        src={internship.thumbnailImgSrc}
                        className="thumbnail"
                      />
                      <div>
                        <StyledFeatureInternItemTitle type="SubHeading">
                          {internship.title}
                        </StyledFeatureInternItemTitle>
                        <User
                          type="Small"
                          name={internship.companyName}
                          iconPath={internship.logoImgSrc}
                          description={internship.companyName}
                        />
                      </div>
                    </StyledFeatureInternItem>
                  ))}
                </StyledFeatureItem> */}
              </StyledContainerMainArea>
            </StyledContainer>
          </StyledContainerLarge>
          <StyledPromoteArea>
            <StyledPromoteAreaInner>
              <div>
                <StyledPromoteMessage type="SubHero">
                  まずは相談会でアドバイザーに
                  <StyledBrSp />
                  詳しく聞いてみよう！
                </StyledPromoteMessage>
                <StyledPromoteCaption type="SubNote">
                  オンラインでスキマ時間に30分！
                  <StyledBrSp />
                  プログラミングに関するあなたのお悩みに
                  <StyledBrSp />
                  何でもお答えします。
                </StyledPromoteCaption>
              </div>
              <StyledButtonGroup>
                <StyledCtaLink
                  size="Medium"
                  href="http://bit.ly/2vN2HhZ"
                  target="_brank"
                  className="gtm_course-waiting-fot-next-btn"
                >
                  <StyledApplyMethodLine src={staticPath("/static/images/course/course_line_green.svg")} />
                  次回の開講通知を受け取る
                </StyledCtaLink>
                {/* <StyledApplyMethod>
                  参加者限定！最大2万円OFFクーポン進呈
                </StyledApplyMethod>
                <StyledFunctionLink
                  size="Medium"
                  isButton
                  onClick={() => {
                    userStore.user
                      ? uiStore.toggleCourseSchduleSelectModal()
                      : uiStore.toggleAuthPromptModal(
                          internalLink(`/auth/register?redirect_after_auth=${location.pathname}?register-complete_from=consultation`)
                        )
                  }}
                  className="gtm_engineer-course-application"
                >
                  無料相談会を予約する
                </StyledFunctionLink> */}
              </StyledButtonGroup>
            </StyledPromoteAreaInner>
          </StyledPromoteArea>
          <StyledContainerLarge>
            <StyledContainer>
              <StyledContainerTextArea>
                <StyledContainerTextAreaInner>
                  <StyledContainerLabel>PRICE</StyledContainerLabel>
                  <StyledContainerHeading>
                    分割支払いで
                    <br />
                    学生でもお支払い可能な価格
                  </StyledContainerHeading>
                  <StyledContainerCaption type="Body">新卒エンジニアとしての切符を手に入れよう</StyledContainerCaption>
                </StyledContainerTextAreaInner>
              </StyledContainerTextArea>
              <StyledContainerMainArea>
                <StyledPriceContentItem>
                  <PriceContent />
                </StyledPriceContentItem>
                <StyledPriceItemCautionWrapper>
                  <StyledPriceItemCaution type="Description">
                    お支払い方法は
                    <span className="attention">クレジットカード</span>
                    （一括/分割）または
                    <span className="attention">銀行振込</span>（一括）または
                    <span className="attention">口座振替</span>（分割） になります。
                  </StyledPriceItemCaution>
                  <StyledPriceItemCaution type="Description">
                    クレジットカード払いは最大24回まで分割払いが可能です（分割可能回数はカード会社の規定によります）。
                  </StyledPriceItemCaution>
                </StyledPriceItemCautionWrapper>
              </StyledContainerMainArea>
            </StyledContainer>
          </StyledContainerLarge>
          <StyledPromoteArea>
            <StyledPromoteAreaInner>
              <div>
                <StyledPromoteMessage type="SubHero">
                  まずは相談会でアドバイザーに
                  <StyledBrSp />
                  詳しく聞いてみよう！
                </StyledPromoteMessage>
                <StyledPromoteCaption type="SubNote">
                  オンラインでスキマ時間に30分！
                  <StyledBrSp />
                  プログラミングに関するあなたのお悩みに
                  <StyledBrSp />
                  何でもお答えします。
                </StyledPromoteCaption>
              </div>
              <StyledButtonGroup>
                <StyledCtaLink
                  size="Medium"
                  href="http://bit.ly/2vN2HhZ"
                  target="_brank"
                  className="gtm_course-waiting-fot-next-btn"
                >
                  <StyledApplyMethodLine src={staticPath("/static/images/course/course_line_green.svg")} />
                  次回の開講通知を受け取る
                </StyledCtaLink>
                {/* <StyledApplyMethod>
                  参加者限定！最大2万円OFFクーポン進呈
                </StyledApplyMethod>
                <StyledFunctionLink
                  size="Medium"
                  isButton
                  onClick={() => {
                    userStore.user
                      ? uiStore.toggleCourseSchduleSelectModal()
                      : uiStore.toggleAuthPromptModal(
                          internalLink(`/auth/register?redirect_after_auth=${location.pathname}?register-complete_from=consultation`)
                        )
                  }}
                  className="gtm_engineer-course-application"
                >
                  無料相談会を予約する
                </StyledFunctionLink> */}
              </StyledButtonGroup>
            </StyledPromoteAreaInner>
          </StyledPromoteArea>
          <StyledContainerLarge>
            <StyledContainer>
              <StyledContainerTextArea>
                <StyledContainerTextAreaInner>
                  <StyledContainerLabel>CONSULTATION</StyledContainerLabel>
                  <StyledContainerHeading>
                    エンジニアとして一歩踏み出そう
                    <br />
                    無料相談会
                  </StyledContainerHeading>
                  <StyledContainerCaption type="Body">
                    アドバイザーに講座やキャリアについて
                    <br />
                    気軽に相談してみよう
                  </StyledContainerCaption>
                </StyledContainerTextAreaInner>
              </StyledContainerTextArea>
              <StyledContainerMainArea>
                <StyledConsultationItem>
                  <StyledConsultationItemIcon src={staticPath("/static/images/course/course_clock.svg")} />
                  <StyledConsultationItemTextArea>
                    <StyledConsultationItemTextAreaHeading>
                      30分程度のオンライン面談
                    </StyledConsultationItemTextAreaHeading>
                    <StyledConsultationItemTextAreaCaption type="Description">
                      忙しい学生生活のスキマ時間で気軽に実施できます
                    </StyledConsultationItemTextAreaCaption>
                  </StyledConsultationItemTextArea>
                </StyledConsultationItem>
                <StyledConsultationItem>
                  <StyledConsultationItemIcon src={staticPath("/static/images/course/course_light.svg")} />
                  <StyledConsultationItemTextArea>
                    <StyledConsultationItemTextAreaHeading>
                      あなたに合ったコースをご提案
                    </StyledConsultationItemTextAreaHeading>
                    <StyledConsultationItemTextAreaCaption type="Description">
                      現在のスキルや経験に合った講座の進め方を考えます
                    </StyledConsultationItemTextAreaCaption>
                  </StyledConsultationItemTextArea>
                </StyledConsultationItem>
                <StyledConsultationItem>
                  <StyledConsultationItemIcon src={staticPath("/static/images/course/course_message.svg")} />
                  <StyledConsultationItemTextArea>
                    <StyledConsultationItemTextAreaHeading>キャリア相談も実施</StyledConsultationItemTextAreaHeading>
                    <StyledConsultationItemTextAreaCaption type="Description">
                      就活やエンジニアの仕事ついての相談にもお応えします
                    </StyledConsultationItemTextAreaCaption>
                  </StyledConsultationItemTextArea>
                </StyledConsultationItem>
                <StyledConsultationCaution type="Description">
                  ※無理な勧誘は一切行いませんので安心してお申し込みください。
                </StyledConsultationCaution>
              </StyledContainerMainArea>
            </StyledContainer>
          </StyledContainerLarge>
          <StyledPromoteArea>
            <StyledPromoteAreaInner>
              <div>
                <StyledPromoteMessage type="SubHero">
                  まずは相談会でアドバイザーに
                  <StyledBrSp />
                  詳しく聞いてみよう！
                </StyledPromoteMessage>
                <StyledPromoteCaption type="SubNote">
                  オンラインでスキマ時間に30分！
                  <StyledBrSp />
                  プログラミングに関するあなたのお悩みに
                  <StyledBrSp />
                  何でもお答えします。
                </StyledPromoteCaption>
              </div>
              <StyledButtonGroup>
                <StyledCtaLink
                  size="Medium"
                  href="http://bit.ly/2vN2HhZ"
                  target="_brank"
                  className="gtm_course-waiting-fot-next-btn"
                >
                  <StyledApplyMethodLine src={staticPath("/static/images/course/course_line_green.svg")} />
                  次回の開講通知を受け取る
                </StyledCtaLink>
                {/* <StyledApplyMethod>
                  参加者限定！最大2万円OFFクーポン進呈
                </StyledApplyMethod>
                <StyledFunctionLink
                  size="Medium"
                  isButton
                  onClick={() => {
                    userStore.user
                      ? uiStore.toggleCourseSchduleSelectModal()
                      : uiStore.toggleAuthPromptModal(
                          internalLink(`/auth/register?redirect_after_auth=${location.pathname}?register-complete_from=consultation`)
                        )
                  }}
                  className="gtm_engineer-course-application"
                >
                  無料相談会を予約する
                </StyledFunctionLink> */}
              </StyledButtonGroup>
            </StyledPromoteAreaInner>
          </StyledPromoteArea>
          {/* <StyledFixedAppealPcWrapper>
            <StyledeFixedAppealPc>
              <p className="kick-off">
                第5期生
                <span className="date">
                  9/30<span className="day-of-the-week"> (水)</span>
                </span>
                キックオフ
              </p>
              <p className="capacity">
                特別キャンペーン実施中
              </p>
              <p className="discount">
                受講後に長期インターンに参加すると
                <span className="amount">
                  <span className="number">50,000</span>円割引
                </span>
              </p>
            </StyledeFixedAppealPc>
          </StyledFixedAppealPcWrapper>
          <StyledeFixedAppealSp>
            <p className="kick-off">
              第5期生
              <span className="date">
                9/30<span className="day-of-the-week"> (水)</span>
              </span>
              キックオフ
            </p>
            <p className="capacity">
            キャンペーン
              <span className="remain">
                実施中
              </span>
            </p>
            <p className="discount">
              受講後に長期インターン参加で
              <span className="amount">
                <span className="number">50,000</span>円割引
              </span>
            </p>
          </StyledeFixedAppealSp> */}
          <Observer>
            {() => (
              <>
                <CourseInputPersonalDataModal
                  open={uiStore.authPromptModalOpen}
                  close={() => uiStore.toggleAuthPromptModal()}
                  updateTmpUserInfo={updateTmpUserInfo}
                  mainAction={() => {
                    uiStore.toggleAuthPromptModal()
                    uiStore.toggleCourseSchduleSelectModal()
                  }}
                />
                <CourseSchduleSelectModal
                  open={uiStore.courseSchduleSelectModalOpen}
                  selectedScheduleValues={selectedScheduleValues}
                  onSelectSchedule={handleSelectSchedule}
                  mainAction={mainActionForSchduleSelectModal}
                  subAction={() => uiStore.toggleCourseSchduleSelectModal()}
                  isLoading={isLoading}
                  availableLateNight={true}
                  excludeDateStart="2021-05-22"
                  excludeDateEnd="2021-05-24"
                />
                <CourseApplyDoneModal
                  open={uiStore.completeApplyCourseModalOpen}
                  close={() => uiStore.toggleCompleteApplyCourseModal()}
                  addFriend={() => window.open(course.lineUrl)}
                  lineQrImageUrl={course.lineQrImageUrl}
                />
              </>
            )}
          </Observer>
        </Container>
        <Footer />
      </>
    )
  }
}

const Container = styled.div`
  position: relative;
  ${IsMobile(css`
    overflow: hidden;
  `)};
`

const StyledOverGrayArea = styled.div`
  width: 100%;
  padding: ${SpacingSize["4XL"]}px 0;
  background-color: ${Color.BackgroundGray};
  ${IsMobile(css`
    padding: 0;
  `)};
`

const StyledMainVisual = styled.div`
  max-width: 1000px;
  position: relative;
  margin: auto;
  border-radius: ${RadiusSize.Medium}px;
  background: -moz-linear-gradient(135deg, rgba(0, 150, 150, 0.85), rgba(38, 166, 166, 0.85)),
    url(${staticPath("/static/images/course/course_engineer_main.png")});
  background: -webkit-linear-gradient(135deg, rgba(0, 150, 150, 0.85), rgba(38, 166, 166, 0.85)),
    url(${staticPath("/static/images/course/course_engineer_main.png")});
  background: linear-gradient(135deg, rgba(0, 150, 150, 0.85), rgba(38, 166, 166, 0.85)),
    url(${staticPath("/static/images/course/course_engineer_main.png")});
  padding: 52px 64px 52px 64px;
  background-position: center;
  background-size: cover;
  ${IsMobile(css`
    border-radius: 0;
    padding: 80px 0 64px;
    text-align: center;
  `)};
`

const StyledMainVisualSupplement = styled.p`
  font-weight: bold;
  color: ${Color.White};
  font-size: ${TextSize.S}px;
`

const StyledMainVisualInner = styled.div`
  ${IsPC(css`
    display: flex;
    align-items: center;
  `)}
  ${IsMobile(css`
    display: inline-block;
    width: 100%;
    padding: 0 7.2vw;
  `)}
`
const StyledMainVisualText = styled.div`
  text-align: left;
  ${IsMobile(css`
    margin-bottom: 52px;
  `)}
`
const StyledMainVisualSubTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`
const StyledMainVisualSubTitleLeft = styled.p`
  font-size: ${TextSize.S}px;
  line-height: ${LineHeight.S}%;
  font-weight: bold;
  color: ${Color.White};
  border: 1px solid ${Color.White};
  border-radius: ${RadiusSize.Medium}px;
  padding: ${SpacingSize.XS}px ${SpacingSize.S}px;
  margin-right: ${SpacingSize.M}px;
  ${IsMobile(css`
    font-size: 12px;
    margin-right: 12px;
  `)}
`
const StyledMainVisualSubTitleRight = styled.p`
  flex: 1;
  font-size: ${TextSize.M}px;
  line-height: ${LineHeight.M}%;
  color: ${Color.White};
  ${IsMobile(css`
    font-size: 12px;
    line-height: ${LineHeight.S}%;
  `)}
`
const StyledMainVisualSubTitleRightBold = styled.span`
  font-weight: bold;
`
const StyledMainVisualTitle = styled.p`
  font-size: 32px;
  line-height: ${LineHeight.M}%;
  font-weight: bold;
  color: ${Color.White};
  margin: ${SpacingSize["3XS"]}px 0 ${SpacingSize.L}px;
  text-shadow: 0 2px 12px rgba(33, 33, 33, 0.2);
  ${IsMobile(css`
    font-size: 24px;
    line-height: 180%;
    margin: 0 0 12px;
  `)}
`

const StyledBeginner = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  img {
    ${IsPC(css`
      border-top-right-radius: 8px;
    `)}
  }
  .text {
    position: absolute;
    top: 12px;
    right: 4px;
    text-align: center;
    line-height: 120%;
    transform: rotate(45deg);
    font-size: ${TextSize.M}px;
    font-weight: bold;
    color: #fff;
  }
`

const StyledTopButtonGroup = styled.div`
  max-width: 280px;
  width: 100%;
  text-align: center;
  margin: 0 0 0 auto;
  &.left-align {
    margin: 0;
  }
  ${IsMobile(css`
    margin: 0 auto;
  `)}
`

const StyledButtonGroup = styled.div`
  max-width: 280px;
  width: 100%;
  text-align: center;
  &.left-align {
    margin: 0;
  }
  ${IsMobile(css`
    margin: auto;
  `)}
`
const StyledApplyMethod = styled.p`
  position: relative;
  display: inline-block;
  font-size: ${TextSize.S}px;
  font-weight: bold;
  color: ${Color.White};
  margin-bottom: 10px;
  &:before {
    position: absolute;
    content: "";
    width: 2px;
    height: 12px;
    background: ${Color.White};
    transform: rotate(30deg);
    right: -8px;
    bottom: 0;
  }
  &:after {
    position: absolute;
    content: "";
    width: 2px;
    height: 12px;
    background: ${Color.White};
    transform: rotate(-30deg);
    left: -8px;
    bottom: 0;
  }
  .attention {
    color: #fcb809;
  }
`
const StyledApplyMethodLine = styled.img`
  width: 20px;
  display: inline-block;
  margin-right: ${SpacingSize["2XS"]}px;
  margin-bottom: -4px;
  margin-top: -2px;
`
const StyledCtaLink = styled(CtaLink)`
  color: ${Color.Primary};
  background-color: ${Color.White};
  ${Shadow.Deep};
  &:hover {
    background-color: ${Color.White};
    opacity: 0.8;
  }
`
const StyledFunctionLink = styled(FunctionLink)`
  color: ${Color.Primary};
  background-color: ${Color.White};
  border: none;
  ${Shadow.Deep};
  &:hover {
    color: ${Color.Primary};
    background-color: ${Color.White};
    border: none;
    opacity: 0.8;
  }
`
const StyledContainerLarge = styled(ContainerLarge)`
  ${IsMobile(css`
    padding: 0 7.2vw;
  `)}
`
const StyledContainer = styled.div`
  display: flex;
  margin: 96px 0 0;
  width: 100%;
  &:last-of-type {
    margin-bottom: 96px;
  }
  ${IsMobile(css`
    display: inline-block;
    margin: 96px 0 0;
    &:first-of-type {
      margin-top: 48px;
    }
    &:last-of-type {
      margin-bottom: 48px;
    }
  `)}
`
const StyledContainerTextArea = styled.div`
  flex: 1;
  text-align: left;
  top: 0;
  ${IsMobile(css`
    margin-bottom: 36px;
  `)}
`
const StyledContainerMainArea = styled.div`
  flex: 1;
  text-align: left;
`
const StyledContainerTextAreaInner = styled.div`
  position: sticky;
  position: -webkit-sticky;
  top: ${SpacingSize["4XL"]}px;
  ${IsMobile(css`
    position: relative;
    top: 0;
  `)}
`
const StyledContainerLabel = styled.p`
  font-size: ${TextSize.M}px;
  font-weight: bold;
  line-height: ${LineHeight["2XS"]}%;
  color: ${Color.Primary};
  margin-bottom: ${SpacingSize.XS}px;
  ${IsMobile(css`
    font-size: 12px;
  `)}
`
const StyledContainerHeading = styled.p`
  font-size: ${TextSize["2XL"]}px;
  font-weight: bold;
  line-height: ${LineHeight.S}%;
  color: ${Color.Black};
  margin-bottom: ${SpacingSize.XS}px;
  ${IsMobile(css`
    font-size: 20px;
  `)}
  .attention {
    color: ${Color.Attention};
  }
`
const StyledContainerCaption = styled(Typography)`
  color: ${Color.Gray};
  ${IsMobile(css`
    font-size: 12px;
    line-height: 160%;
  `)}
`
const StyledContainerCaptionSupplement = styled(Typography)`
  color: ${Color.Gray};
  margin-top: ${SpacingSize["3XS"]}px;
  ${IsMobile(css`
    font-size: 10px;
    line-height: 160%;
  `)}
`

const StyledBackgroundHeading = styled(Typography)`
  text-align: center;
  color: ${Color.White};
  background: ${Color.Primary};
  padding: ${SpacingSize.S}px;
  border-radius: ${RadiusSize.Medium}px;
  margin-bottom: ${SpacingSize.L}px;
  ${IsMobile(css`
    padding: 8px;
  `)}
`
const StyledBackgroundItemArea = styled.div`
  display: flex;
  margin: -${SpacingSize["2XS"]}px;
`
const StyledBackgroundItem = styled.div`
  width: 50%;
  text-align: center;
  padding: ${SpacingSize.XS}px ${SpacingSize.XS}px ${SpacingSize.XL}px;
  background: ${Color.White};
  border-radius: ${RadiusSize.Medium}px;
  ${Shadow.Deep};
  margin: ${SpacingSize["2XS"]}px;
  ${IsMobile(css`
    padding-bottom: 20px;
  `)}
`
const StyledBackgroundItemIcon = styled.img`
  width: 120px;
  ${IsMobile(css`
    width: 108px;
  `)}
`
const StyledBackgroundItemText = styled(Typography)`
  color: ${Color.Black};
  ${IsMobile(css`
    font-size: 14px;
  `)}
`
const StyledBackgroundArrowWrapper = styled.div`
  text-align: center;
  margin: ${SpacingSize.XL}px 0;
`
const StyledBackgroundArrow = styled.div`
  display: inline-block;
  bottom: -4px;
  left: -6px;
  content: "";
  border-style: solid;
  border-width: 24px 40px 0 40px;
  border-color: ${Color.BorderGray} transparent transparent transparent;
  ${IsMobile(css`
    border-width: 20px 32px 0 32px;
  `)}
`
const StyledBackgroundAfter = styled.div`
  text-align: center;
  padding: ${SpacingSize.XL}px 0;
  font-size: ${TextSize.XL}px;
  font-weight: bold;
  line-height: ${LineHeight.S}%;
  color: ${Color.White};
  background: ${Color.Secondary};
  border-radius: ${RadiusSize.Medium}px;
  ${IsMobile(css`
    font-size: 20px;
    padding: 20px 0;
  `)}
`
const StyledBackgroundAfterSupplement = styled.p`
  font-size: ${TextSize.M}px;
  line-height: ${LineHeight.M}%;
  font-weight: normal;
  color: ${Color.White};
  ${IsMobile(css`
    font-size: 14px;
    margin-bottom: 4px;
  `)}
`

const GraduatesContainer = styled.div`
  margin-top: ${SpacingSize["2XL"]}px;
  display: flex;
  flex-flow: column;
  justify-content: space-evenly;

  .each-graduate {
    margin-bottom: ${SpacingSize["3XL"]}px;
    display: flex;
    align-items: center;
    text-align: center;
    color: ${Color.Gray};
    &:last-child {
      margin-bottom: 0;
    }
    ${IsMobile(css`
      flex-flow: column;
    `)}
  }
  .graduate-thumbnail {
    width: 140px;
    height: 140px;
    margin: 0 ${SpacingSize.L}px ${SpacingSize.S}px 0;
    border-radius: 50%;
  }
  .comment {
    margin-bottom: ${SpacingSize.XS}px;
    font-size: ${TextSize.S}px;
    font-weight: bold;
    line-height: ${LineHeight.M}%;
  }
  .profile {
    font-size: ${TextSize.S}px;
    line-height: ${LineHeight.S}%;
  }
`

const StyledSolutionLink = styled(TransitionLink)`
  display: inline;
  font-weight: normal;
  ${IsMobile(css`
    font-size: 10px;
  `)}
`
const StyledSolutionFlow = styled.div`
  margin-bottom: 96px;
  ${IsMobile(css`
    margin-bottom: 48px;
  `)}
`
const StyledSolutionFlowBackgroundGroup = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  margin-bottom: -348px;
  ${IsMobile(css`
    margin-bottom: -330px;
  `)}
`
const StyledSolutionFlowBackground = styled.div`
  position: relative;
  content: "";
  width: calc(50% - 8px);
  height: 340px;
  z-index: -1;
  background: ${Color.BackgroundSecondary};
  border-radius: ${RadiusSize.Medium}px;
  ${IsMobile(css`
    height: 322px;
    top: 0px;
  `)}
  &:last-of-type {
    background: ${Color.BackgroundGray};
    height: 240px;
    ${IsMobile(css`
      height: 228px;
      top: 0px;
    `)}
  }
`
const StyledSolutionFlowBranchGroup = styled.div`
  display: flex;
  margin: -${SpacingSize.XS}px;
  margin-bottom: ${SpacingSize.XS}px;
`
const StyledSolutionFlowBranchItem = styled.div`
  width: 50%;
  text-align: center;
  font-size: ${TextSize.XS}px;
  font-weight: bold;
  line-height: ${LineHeight["2XS"]}%;
  padding: 11px 0;
  margin: ${SpacingSize.XS}px;
  color: ${Color.White};
  background: ${Color.Secondary};
  ${Shadow.Deep};
  border-radius: ${RadiusSize.Medium}px;
  &:last-of-type {
    background: ${Color.LightGray};
  }
  ${IsMobile(css`
    font-size: 10px;
  `)}
`
const StyledSolutionFlowItem = styled(Typography)`
  text-align: center;
  padding: ${SpacingSize.S}px ${SpacingSize.XS}px;
  margin-bottom: ${SpacingSize.L}px;
  color: ${Color.Black};
  background: ${Color.White};
  ${Shadow.Deep};
  border-radius: ${RadiusSize.Medium}px;
  ${IsMobile(css`
    font-size: 14px;
  `)}
`
const StyledSolutionFlowItemHalf = styled(Typography)`
  width: calc(50% - 8px);
  text-align: center;
  color: ${Color.Secondary};
  padding: 0 ${SpacingSize.XS}px ${SpacingSize.XS}px;
  background: ${Color.White};
  ${Shadow.Deep};
  border-radius: ${RadiusSize.Medium}px;
  ${IsMobile(css`
    font-size: 14px;
  `)}
`
const StyledSolutionFlowItemHalfLabel = styled.div`
  font-size: ${TextSize.XS}px;
  font-weight: bold;
  line-height: ${LineHeight["2XS"]}%;
  color: ${Color.Secondary};
  padding: ${SpacingSize["3XS"]}px 0;
  margin: 0 -${SpacingSize.XS}px ${SpacingSize.XS}px;
  margin-bottom: ${SpacingSize.XS}px;
  background: ${Color.BackgroundSecondary};
  ${IsMobile(css`
    font-size: 10px;
  `)}
`
const StyledSolutionArrowGroup = styled.div`
  display: flex;
  margin: ${SpacingSize.S}px -${SpacingSize.S}px ${SpacingSize["3XS"]}px;
`
const StyledSolutionArrowWrapper = styled.div`
  width: 50%;
  text-align: center;
  margin: 8px;
`
const StyledSolutionArrow = styled.div`
  display: inline-block;
  bottom: -4px;
  left: -6px;
  content: "";
  border-style: solid;
  border-width: 12px 18px 0 18px;
  border-color: ${Color.BorderGray} transparent transparent transparent;
  ${IsMobile(css`
    border-width: 12px 18px 0 18px;
  `)}
`
const StyledSolutionFlowItemLastGroup = styled.div`
  display: flex;
  margin: -${SpacingSize.XS}px;
`
const StyledSolutionFlowItemLastItem = styled(Typography)`
  width: 50%;
  text-align: center;
  padding: ${SpacingSize.S}px ${SpacingSize.XS}px;
  margin: ${SpacingSize.XS}px;
  color: ${Color.White};
  background: ${Color.Secondary};
  ${Shadow.Deep};
  border-radius: ${RadiusSize.Medium}px;
  &:last-of-type {
    background: ${Color.LightGray};
  }
`
const StyledSolutionGraph = styled.div``
const StyledSolutionGraphHeading = styled(Typography)`
  margin-bottom: ${SpacingSize.XL}px;
  ${IsMobile(css`
    font-size: 16px;
    margin-bottom: 20px;
  `)}
`
const StyledSolutionGraphImagePc = styled.img`
  width: 100%;
  background-position: center;
  background-size: cover;
  position: relative;
  ${IsMobile(css`
    display: none;
  `)}
`
const StyledSolutionGraphImageSp = styled.img`
  width: 100%;
  background-position: center;
  background-size: cover;
  position: relative;
  ${IsPC(css`
    display: none;
  `)}
`

const StyledSolutionGraphDescription = styled.div`
  border: 1px solid ${Color.LightGray};
  border-radius: ${RadiusSize.Medium}px;
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: ${SpacingSize.M}px;
  padding: ${SpacingSize.S}px 0;
  ${IsMobile(css`
    padding: 8px 0;
  `)}
`
const StyledSolutionGraphDescriptionLeft = styled.div`
  text-align: center;
  font-size: ${TextSize.S}px;
  line-height: ${LineHeight.S}%;
  font-weight: bold;
  color: ${Color.LightGray};
  padding: 0 ${SpacingSize.S}px;
  border-right: 1px solid ${Color.LightGray};
  ${IsMobile(css`
    font-size: 12px;
  `)}
`
const StyledSolutionGraphDescriptionRight = styled.div`
  flex: 1;
  font-size: ${TextSize.S}px;
  line-height: ${LineHeight.M}%;
  color: ${Color.LightGray};
  padding: 0 ${SpacingSize.S}px;
  ${IsMobile(css`
    font-size: 12px;
  `)}
`
const StyledSolutionGraphDescriptionRightEmphasis = styled.span`
  font-weight: bold;
  color: ${Color.Secondary};
`

const StyledFeatureItem = styled.div`
  margin: ${SpacingSize["3XL"]}px 0;
  &:first-of-type {
    margin-top: 0;
  }
  &:last-of-type {
    margin-bottom: 0;
  }
  ${IsMobile(css`
    margin: 32px 0;
  `)}
`
const StyledFeatureItemImage1 = styled.div`
  width: 100%;
  height: 250px;
  border-radius: ${RadiusSize.Medium}px;
  background-image: url(${staticPath("/static/images/course/course_engineer_feature_1.png")});
  background-position: center;
  background-size: cover;
  margin-bottom: ${SpacingSize.XL}px;
  ${IsMobile(css`
    height: 160px;
    margin-bottom: 20px;
  `)}
`
const StyledFeatureItemImage2 = styled.div`
  width: 100%;
  height: 250px;
  border-radius: ${RadiusSize.Medium}px;
  background-image: url(${staticPath("/static/images/course/course_engineer_feature_2.png")});
  background-position: center;
  background-size: cover;
  margin-bottom: ${SpacingSize.XL}px;
  ${IsMobile(css`
    height: 160px;
    margin-bottom: 20px;
  `)}
`
const StyledFeatureItemImage3 = styled.div`
  width: 100%;
  height: 250px;
  border-radius: ${RadiusSize.Medium}px;
  background-image: url(${staticPath("/static/images/course/course_engineer_feature_3.png")});
  background-position: center;
  background-size: cover;
  margin-bottom: ${SpacingSize.XL}px;
  ${IsMobile(css`
    height: 160px;
    margin-bottom: 20px;
  `)}
`
const StyledFeatureItemImage4 = styled.div`
  width: 100%;
  height: 250px;
  border-radius: ${RadiusSize.Medium}px;
  background-image: url(${staticPath("/static/images/course/course_engineer_feature_4.jpg")});
  background-position: center;
  background-size: cover;
  margin-bottom: ${SpacingSize.XL}px;
  ${IsMobile(css`
    height: 160px;
    margin-bottom: 20px;
  `)}
`
const StyledFeatureItemTitle = styled(Typography)`
  ${IsMobile(css`
    font-size: 16px;
  `)}
`
const StyledFeatureItemCaption = styled(Typography)`
  margin-top: ${SpacingSize.XS}px;
  color: ${Color.Gray};
  ${IsMobile(css`
    font-size: 14px;
  `)}
`
const StyledFeatureArrowWrapper = styled.div`
  text-align: center;
  margin: ${SpacingSize["3XL"]}px 0;
  ${IsMobile(css`
    margin: 32px 0;
  `)}
`
const StyledFeatureArrow = styled.div`
  display: inline-block;
  bottom: -4px;
  left: -6px;
  content: "";
  border-style: solid;
  border-width: 24px 40px 0 40px;
  border-color: ${Color.BorderGray} transparent transparent transparent;
  ${IsMobile(css`
    border-width: 20px 32px 0 32px;
  `)}
`
const StyledFeatureInternsHeading = styled.div`
  text-align: center;
  padding: ${SpacingSize.M}px 0;
  font-size: 18px;
  font-weight: bold;
  line-height: ${LineHeight.S}%;
  color: ${Color.Secondary};
  border: 1px solid ${Color.Secondary};
  border-radius: ${RadiusSize.Medium}px;
  /* margin-bottom: ${SpacingSize["2XL"]}px; */
  ${IsMobile(css`
    font-size: 16px;
    padding: 12px 0;
    /* margin-bottom: 24px; */
  `)}
`

const StyledFeatureInternItem = styled.a`
  margin-bottom: ${SpacingSize.XL}px;
  display: flex;
  align-items: center;
  ${IsMobile(css`
    margin-bottom: 20px;
  `)}
  &:last-of-type {
    margin: 0;
  }
  &:hover {
    .thumbnail {
      border: 1px solid ${Color.Primary};
    }
  }
`
const StyledFeatureInternItemImage = styled.img`
  width: 140px;
  height: 90px;
  border-radius: ${RadiusSize.Medium}px;
  object-fit: cover;
  margin-right: ${SpacingSize.M}px;
  ${IsMobile(css`
    width: 100px;
    height: 80px;
    margin-right: 12px;
  `)}
`
const StyledFeatureInternItemTitle = styled(Typography)`
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: ${SpacingSize["3XS"]}px;
`
const StyledContentItem = styled.div`
  position: relative;
  text-align: center;
  background-color: ${Color.White};
  ${Shadow.Deep};
  padding: ${SpacingSize["2XL"]}px ${SpacingSize.XL}px;
  border-radius: ${RadiusSize.Medium}px;
  &:first-of-type {
    margin-bottom: ${SpacingSize["3XL"]}px;
  }
`

const StyledContentItemSubTitle = styled.p`
  color: ${Color.Primary};
  font-size: ${TextSize.M}px;
  font-weight: bold;
  margin-bottom: 6px;
  ${IsMobile(css`
    font-size: 14px;
  `)}
`
const StyledContentItemTitle = styled(Typography)`
  ${IsMobile(css`
    font-size: 24px;
  `)}
`
const StyledPriceContentWrapeer = styled.div`
  margin-top: 12px;
`
const StyledContentItemPointLabel = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: ${Color.LightGray};
  border: 1px solid ${Color.LightGray};
  border-radius: 16px;
  padding: 6px ${SpacingSize.M}px;
  margin: ${SpacingSize.XL}px auto ${SpacingSize.S}px;
  width: max-content;
  ${IsMobile(css`
    padding: 4px 12px;
  `)}
`
const StyledContentItemPointInner = styled.div`
  text-align: left;
`
const StyledContentItemPointRecomends = styled.ul`
  list-style-type: disc;
  padding-left: ${SpacingSize.XL}px;
  font-size: ${TextSize.M}px;
  line-height: ${LineHeight.L}%;
  ${IsMobile(css`
    font-size: 14px;
    padding-left: 20px;
  `)}
`
const StyledContentItemTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${SpacingSize.XS}px;
  ${IsMobile(css`
    display: inline-block;
    margin-bottom: 10px;
  `)}
`
const StyledContentItemSpFlexArea = styled.div`
  display: flex;
  align-items: center;
`
const StyledContentItemLabel = styled.div`
  font-size: ${TextSize.XS}px;
  font-weight: bold;
  line-height: ${LineHeight["2XS"]}%;
  color: ${Color.White};
  background-color: ${Color.LightGray};
  padding: 6px 0;
  margin-right: ${SpacingSize.S}px;
  width: 60px;
  text-align: center;
  border-radius: ${RadiusSize.Medium}px;
  ${IsMobile(css`
    font-size: 10px;
    margin: 0 12px 0 0;
  `)}
`
const StyledContentItemStep = styled(Typography)`
  color: ${Color.Primary};
  ${IsMobile(css`
    font-size: 20px;
  `)}
`
const StyledContentItemDescription = styled.p`
  font-size: ${TextSize.M}px;
  font-weight: bold;
  line-height: ${LineHeight["2XS"]}%;
  margin-left: ${SpacingSize.S}px;
  ${IsMobile(css`
    margin: 8px 0 0;
    font-size: 14px;
    line-height: 100%;
  `)}
`
const StyledContentItemCaption = styled(Typography)`
  color: ${Color.Gray};
  ${IsMobile(css`
    font-size: 14px;
    line-height: 180%;
  `)}
`
const StyledContentArrowWrapper = styled.div`
  text-align: center;
  margin: ${SpacingSize.S}px 0;
`
const StyledContentArrow = styled.div`
  display: inline-block;
  bottom: -4px;
  left: -6px;
  content: "";
  border-style: solid;
  border-width: 24px 40px 0 40px;
  border-color: ${Color.BorderGray} transparent transparent transparent;
  ${IsMobile(css`
    border-width: 20px 32px 0 32px;
  `)}
`

const StyledPriceContentItem = styled.div`
  position: relative;
  text-align: center;
  background-color: ${Color.White};
  ${Shadow.Deep};
  padding: ${SpacingSize["2XL"]}px ${SpacingSize.XL}px;
  border-radius: ${RadiusSize.Medium}px;
  &:first-of-type {
    margin-bottom: ${SpacingSize["4XL"]}px;
  }
`

const StyledContentItemPriceArea = styled.div`
  padding: ${SpacingSize.L}px 0;
  border-radius: ${RadiusSize.Medium}px;
  background-color: ${Color.BackgroundGray};
  ${IsMobile(css`
    padding: 16px 0;
  `)}
`
const StyledContentItemPriceSupplement = styled(Typography)`
  color: ${Color.LightGray};
  .attention {
    color: ${Color.Attention};
  }
  ${IsMobile(css`
    font-size: 12px;
  `)}
`
const StyledPriceItemCardPrice = styled.p`
  font-size: 32px;
  font-weight: bold;
  line-height: ${LineHeight["2XS"]}%;
  color: ${Color.Primary};
  margin: ${SpacingSize.S}px 0 6px;
  ${IsMobile(css`
    font-size: 24px;
    margin: 10px 0 4px;
  `)}
`
const StyledPriceItemCardPriceTax = styled.span`
  font-size: ${TextSize.XS}px;
  ${IsPC(css`
    font-size: 16px;
  `)}
`
const StyledPriceItemCardPer = styled.span`
  font-size: ${TextSize.XS}px;
  font-weight: bold;
  line-height: ${LineHeight["2XS"]}%;
  color: ${Color.Primary};
  ${IsPC(css`
    font-size: 16px;
  `)}
`
const StyledPriceItemCardPriceSumWrapper = styled.div`
  display: flex;
  width: max-content;
  margin: 0 auto;
  background: ${Color.White};
  padding: ${SpacingSize.S}px;
  border-radius: ${RadiusSize.Medium}px;
`
const StyledPriceItemCardPriceSum = styled.div`
  font-size: ${TextSize.XS}px;
  font-weight: bold;
  color: ${Color.Gray};
`
const StyledPriceItemCautionWrapper = styled.div`
  margin-top: ${SpacingSize.XL}px;
`
const StyledPriceItemCaution = styled(Typography)`
  color: ${Color.LightGray};
  padding-left: 1em;
  .attention {
    color: ${Color.Primary};
  }
  &:before {
    content: "※";
    margin-left: -1em;
  }
  ${IsMobile(css`
    font-size: 12px;
  `)}
`

const StyledConsultationItem = styled.div`
  ${Shadow.Deep};
  border-radius: ${RadiusSize.Medium}px;
  display: flex;
  align-items: center;
  padding: ${SpacingSize.L}px ${SpacingSize["2XL"]}px;
  margin-bottom: ${SpacingSize.L}px;
  ${IsMobile(css`
    padding: 16px;
    margin-bottom: 16px;
  `)}
  &:last-of-type {
    margin: 0;
  }
`
const StyledConsultationItemIcon = styled.img`
  ${IsMobile(css`
    width: 40px;
    height: auto;
  `)}
`
const StyledConsultationItemTextArea = styled.div`
  text-align: left;
  margin-left: ${SpacingSize["XL"]}px;
  ${IsMobile(css`
    margin-left: 12px;
  `)}
`

const StyledConsultationItemTextAreaHeading = styled.p`
  font-size: 18px;
  font-weight: bold;
  line-height: ${LineHeight.S}%;
  margin-bottom: 2px;
  ${IsMobile(css`
    font-size: 16px;
    margin-bottom: 4px;
  `)}
`
const StyledConsultationItemTextAreaCaption = styled(Typography)`
  color: ${Color.Gray};
  ${IsMobile(css`
    font-size: 13px;
    line-height: 140%;
  `)}
`

const StyledConsultationCaution = styled(Typography)`
  color: ${Color.LightGray};
  margin-top: ${SpacingSize.XL}px;
  ${IsMobile(css`
    font-size: 12px;
  `)}
`
const StyledPromoteArea = styled.div`
  width: 100%;
  background-color: ${Color.Primary};
  padding: ${SpacingSize["2XL"]}px 16px;
  ${IsMobile(css`
    padding: 32px 7.2vw;
  `)}
`
const StyledPromoteAreaInner = styled.div`
  max-width: 1000px;
  display: flex;
  justify-content: space-between;
  margin: auto;
  align-items: center;
  ${IsMobile(css`
    display: inline-block;
    text-align: center;
    width: 100%;
  `)}
`
const StyledPromoteMessage = styled(Typography)`
  color: ${Color.White};
  margin-bottom: ${SpacingSize.XS}px;
  ${IsMobile(css`
    font-size: 20px;
  `)}
  .sp-hide {
    ${IsMobile(css`
      display: none;
    `)}
  }
`
const StyledPromoteCaption = styled(Typography)`
  font-size: 14px;
  line-height: 140%;
  color: ${Color.White};
  ${IsMobile(css`
    margin-bottom: 24px;
  `)}
`

const StyledBrSp = styled.br`
  ${IsPC(css`
    display: none;
  `)}
`
const StyledBrPc = styled.br`
  ${IsMobile(css`
    display: none;
  `)}
`

// const StyledFixedAppealPcWrapper = styled.div`
//   position: fixed;
//   bottom: 0;
//   left: 0;
//   ${Shadow.Deep};
//   width: 100%;
//   background: ${Color.White};
//   z-index: ${Z.Fixed};
// `
// const StyledeFixedAppealPc = styled.div`
//   max-width: 1000px;
//   margin: auto;
//   width: 100%;
//   background: ${Color.White};
//   display: flex;
//   align-items: center;
//   ${IsMobile(css`
//     display: none;
//   `)}
//   .kick-off {
//     font-size: ${TextSize.M}px;
//     color: ${Color.Gray};
//     padding-left: ${SpacingSize.M}px;
//     .date {
//       color: ${Color.Primary};
//       font-weight: bold;
//       font-size: ${TextSize.L}px;
//       margin: 0 ${SpacingSize["4XS"]}px 0 ${SpacingSize["3XS"]}px;
//       padding: ${SpacingSize["3XS"]}px 0 ${SpacingSize["4XS"]}px;
//     }
//   }
//   .capacity {
//     font-size: ${TextSize.S}px;
//     color: ${Color.White};
//     padding: ${SpacingSize.XS}px ${SpacingSize.M}px;
//     background: ${Color.Attention};
//     border-radius: ${RadiusSize.Medium}px;
//     text-align: center;
//     margin-left: ${SpacingSize.XS}px;
//     .remain {
//       padding-left: ${SpacingSize["3XS"]}px;
//     }
//     .number {
//       font-size: ${TextSize.L}px;
//       font-weight: bold;
//     }
//   }
//   .discount {
//     position: relative;
//     font-size: ${TextSize.M}px;
//     color: ${Color.White};
//     background: ${Color.Primary};
//     text-align: center;
//     width: calc(100% - 450px);
//     margin-left: auto;
//     padding: 14px 0;
//     &:before {
//       content: "";
//       position: absolute;
//       left: 0;
//       top: 0;
//       width: 0;
//       height: 0;
//       border-left: 16px solid ${Color.White};
//       border-top: 24px solid transparent;
//       border-bottom: 24px solid transparent;
//       color: ${Color.White};
//     }
//     .date {
//       font-weight: bold;
//     }
//     .amount {
//       margin-left: ${SpacingSize["4XS"]}px;
//       padding-top: ${SpacingSize["3XS"]}px;
//       font-size: ${TextSize.M}px;
//       font-weight: bold;
//       .number {
//         font-size: ${TextSize.L}px;
//         margin-right: ${SpacingSize["4XS"]}px;
//       }
//     }
//   }
//   .day-of-the-week {
//     font-size: ${TextSize.XS}px;
//   }
// `

// const StyledeFixedAppealSp = styled.div`
//   position: fixed;
//   width: 100%;
//   background: ${Color.White};
//   bottom: 0;
//   display: flex;
//   align-items: center;
//   ${Shadow.Deep};
//   z-index: ${Z.Child};
//   ${IsPC(css`
//     display: none;
//   `)}
//   .kick-off {
//     font-size: ${TextSize.XS}px;
//     color: ${Color.Gray};
//     padding-left: ${SpacingSize.M}px;
//     .date {
//       display: block;
//       color: ${Color.Primary};
//       font-weight: bold;
//       font-size: ${TextSize.M}px;
//       padding: ${SpacingSize["3XS"]}px 0 ${SpacingSize["4XS"]}px;
//     }
//   }
//   .capacity {
//     font-size: ${TextSize["2XS"]}px;
//     color: ${Color.White};
//     padding: ${SpacingSize.XS}px ${SpacingSize.XS}px;
//     background: ${Color.Attention};
//     border-radius: ${RadiusSize.Medium}px;
//     text-align: center;
//     margin-left: ${SpacingSize.XS}px;
//     .remain {
//       display: block;
//       padding-top: ${SpacingSize["4XS"]}px;
//     }
//     .number {
//       font-size: ${TextSize.L}px;
//       font-weight: bold;
//       padding-top: 2px;
//     }
//   }
//   .discount {
//     position: relative;
//     font-size: ${TextSize["2XS"]}px;
//     color: ${Color.White};
//     background: ${Color.Primary};
//     text-align: center;
//     width: calc(100% - 165px);
//     margin-left: auto;
//     padding: ${SpacingSize.S}px 0;
//     &:before {
//       content: "";
//       position: absolute;
//       left: 0;
//       top: 0;
//       width: 0;
//       height: 0;
//       border-left: 20px solid ${Color.White};
//       border-top: 30px solid transparent;
//       border-bottom: 30px solid transparent;
//       color: ${Color.White};
//     }
//     .date {
//       font-weight: bold;
//     }
//     .amount {
//       display: block;
//       padding-top: ${SpacingSize["3XS"]}px;
//       font-size: ${TextSize.M}px;
//       font-weight: bold;
//       .number {
//         font-size: ${TextSize.L}px;
//       }
//     }
//   }
//   .day-of-the-week {
//     font-size: ${TextSize["2XS"]}px;
//   }
// `

// const StyledDialogWrapper = styled.div`
//   width: 80%;
//   max-width: 400px;
//   margin: auto;
//   display: none;
//   position: fixed;
//   top: calc(50% - 450px/2);
//   left: 0;
//   right: 0;
//   z-index: 100;
//   background-color: ${Color.White};
//   border-radius: ${RadiusSize.Medium}px;
//   ${Shadow.Deep};
//   ${({ open }: StyledProps) => css`
//     ${open &&
//       css`
//         display: block;
//       `}
//   `}
// `

// const StyledDialogOverlay = styled.div`
//   position: fixed;
//   z-index: 99;
//   top: -25%;
//   left: 0;
//   bottom: 0;
//   right: 0;
//   height: 125%;
//   width: 100%;
//   background: #000;
//   display: none;
//   opacity: 0.5;
//   ${({ open }: StyledProps) => css`
//     ${open &&
//       css`
//         display: block;
//       `}
//   `}
// `

// const StyledCoverImgArea = styled.div`
//   height: 160px;
//   background-image: url(${staticPath("/static/images/course/course_consultation.png")});
//   background-size: cover;
// `

// const StyledDialogBody = styled.div`
//   padding: ${SpacingSize.XL}px;
//   display: flex;
//   flex-direction: column;
//   align-items: center;
// `

// const StyledDialogHeading = styled.div`
//   margin-bottom: ${SpacingSize["3XS"]}px;
//   font-size: ${TextSize.L}px;
//   font-weight: bold;
//   text-align: center;
//   line-height: ${LineHeight.S}%;
// `

// const StyledDialogContent = styled.div`
//   margin-bottom: ${SpacingSize.M}px;
//   font-size: ${TextSize.S}px;
//   text-align: center;
//   line-height: ${LineHeight.M}%;
//   color: ${Color.Gray};
// `

// const StyledDialogCtaLink = styled(ctaLink)`
//   width: 242px;
//   color: ${Color.White};
//   background-color: ${Color.Primary};
//   ${Shadow.Deep};
//   &:hover {
//     background-color: ${Color.Primary};
//     opacity: 0.8;
//   }
// `

// const StyledDialogCloseLinkWrapper = styled.div`
//   margin-top: ${SpacingSize.S}px;
//   padding: ${SpacingSize.M}px;
// `

export default withRouter(WebProgrammingCourses)
